import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Loading from "../Common/Loading";
import CustomAlert from "../Common/CustomAlert";
import { TextWrap } from "lucide-react";
import popimg from "../Common/Images/ImagesForAlert/popup_bg1_green.png";
import popbg from "../Common/Images/ImagesForAlert/popup_bg2_green.png";
import popclose from "../Common/Images/ImagesForAlert/popup_close_green_n.png";
import tickGreen from "./Image/TickMarkGreen.gif";


const API_BASE_URL =
  process.env.REACT_APP_DOCUMENT_LINK || "https://staging.sysadmin.elearnoncloud.com/";

interface StudentInfo {
  FName: string;
  LName: string;
  Gender: string;
  DOB: string;
  Std: string;
  PointsEarned: number;
  PointsUsed: number;
  CurrentPoints: number;
  MathsTrophies: number;
  EnglishTrophies: number;
  TotalTrophies: number;
  TrophyClubBonus: string;
}

interface WLPSummaryDetails {
  Type: string;
  CreatedOn: string;
  WeeksElapsed: number;
  WeeksLeft: number | string;
  AvlMts: number | string;
  PrevWLPs: string;
  Completed: number;
}

interface WLPStats {
  WkNo: number | string;
  WLPMins: string;
  ToDo: string;
  Done: string;
  Complete: string;
  DoneShifted: string;
  StDone: string;
  ReAssigned: string;
  WLPDone: string;
  DoMore: string;
  AvgScore: string;
  MSMins: string;
}

interface WLPHistoryRow {
  Weekending: string;
  LoggedMts: number | string;
  Maths: Partial<WLPStats>;
  English: Partial<WLPStats>;
}

interface MDTest {
  GradeYear: string;
  ReportNo: number;
  ScoreA: string;
  SetByTutor: string | null;
  SetOn: string | null;
  StartedOn: string | null;
  TestDoneOn: string | null;
}

interface ECTest {
  GradeYear: string;
  ReportNo: number;
  ScoreA: string;
  SetByTutor: string | null;
  SetOn: string | null;
  StartedOn: string | null;
  TestDoneOn: string | null;
}

/* ---------- CHANGED: matches el_showstudentlp_get_lesson_plan response fields exactly ---------- */
interface WLPDetailRow {
  Type: string;
  LessonName: string;
  MDScore: string;
  CompletedFlag: string;
  HowShifted: string;
  Level: string;
  DicFileName: string;
  LessonCode: string;
}
/* -------------------------------------------------------------------------------------------- */

const styles = {
  body: {
    margin: "0px",
    fontFamily: "Trebuchet MS, Arial, Helvetica, sans-serif",
    fontSize: "10pt",
    backgroundColor: "white",
    height: "100vh",
    display: "flex",
    flexDirection: "column" as const,
  },
  container: {
    padding: "5px",
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
  },
  topInfoTable: {
    width: "100%",
    border: "1px solid blue",
    borderCollapse: "collapse" as const,
    marginBottom: "5px",
  },
  topInfoTitle: {
    fontFamily: "Arial",
    fontWeight: "bold",
    borderBottom: "1px solid blue",
    textAlign: "center" as const,
    backgroundColor: "#C7DDFD",
    color: "#2859ac",
    padding: "5px",
    fontSize: "12pt",
    border: "1px solid #7d9cf9 "
  },
  infoCellLabel: {
    fontFamily: "Arial",
    fontSize: "14pt",
    borderBottom: "1px solid blue",
    borderRight: "1px solid blue",
    padding: "5px",
    width: "20%",
    color: "black",
  },
  infoCellValue: {
    fontFamily: "Arial",
    fontSize: "14pt",
    borderBottom: "1px solid blue",
    borderRight: "1px solid blue",
    padding: "5px",
    color: "blue",
  },
  headerRow: {
    fontFamily: "Arial",
    fontSize: "12pt",
    backgroundColor: "#c7ddfd",
    color: "#2859ac",
    height: "32px",
    textAlign: "center" as const,
    TextWrap: "nowrap"
  },
  headerCell: {
    padding: "6px",
    borderRight: "1px solid #7d9cf9",
    borderBottom: "1px solid #7d9cf9",
    textAlign: "center" as const,
    whiteSpace: "nowrap" as const,
    fontWeight: "normal" as const,
    fontSize: "12pt",
  },
  row: {
    fontFamily: "Arial",
    fontSize: "10pt",
    backgroundColor: "white",
    verticalAlign: "top",
    minHeight: "20px",
    color: "black",
    textAlign: "center" as const,
  },
  altRow: {
    fontFamily: "Arial",
    fontSize: "10pt",
    backgroundColor: "#F4FAFF",
    verticalAlign: "top",
    minHeight: "20px",
    color: "black",
    textAlign: "center" as const,
  },
  cell: {
    padding: "8px",
    borderRight: "1px solid #7d9cf9",
    textWrap: "nowrap",
    fontSize: "12pt"
  },
  wlpSummaryBox: {
    border: "1px solid #7d9cf9",
    backgroundColor: "#F4FAFF",
    padding: "5px",
    width: "49%",
    fontFamily: "Arial",
    fontSize: "10pt",
  },
  scrollableDiv: {
    flex: 1,
    overflowY: "auto" as const,
    overflowX: "auto" as const,
    // border: "1px solid blue",
    width: "100%",
    backgroundColor: "white",
    marginBottom: "10px",
  },
  actionLink: {
    color: "blue",
    textDecoration: "underline",
    cursor: "pointer",
    fontFamily: "Arial",
    fontSize: "12pt",
  },
  modalOverlay: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.35)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: "10px",
  },
  modalContent: {
    width: "100%",
    maxWidth: "900px",
    maxHeight: "90%",
    overflowY: "auto" as const,
    backgroundColor: "azure",
    border: "1px solid #7d9cf9",
    borderRadius: "4px",
    padding: "16px",
    boxShadow: "0 0 20px rgba(0,0,0,0.2)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  modalTitle: {
    fontSize: "14pt",
    fontWeight: "bold",
    color: "#1f497d",
    margin: 0,
  },
  modalCloseButton: {
    fontSize: "18px",
    fontWeight: "bold",
    color: "#333",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    lineHeight: 1,
  },
  modalTable: {
    width: "100%",
    borderCollapse: "collapse" as const,
    marginTop: "8px",
  },
  modalTableHeader: {
    textAlign: "left" as const,
    padding: "8px",
    borderBottom: "2px solid #7d9cf9",
    backgroundColor: "#F4FAFF",
    fontSize: "10pt",
  },
  modalTableCell: {
    padding: "8px",
    borderBottom: "1px solid #7d9cf9",
    fontSize: "10pt",
    color: "#000",
  },
};


/* ---------- CHANGED: styles to replicate VB gfn_Popup(theme=green) exactly ---------- */
const wlpModalStyles = {
  overlay: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(180, 180, 180, 0.55)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,

  },
  popup: {
    width: "900px",
    maxWidth: "95vw",
    height: "500px",
    maxHeight: "90vh",
    backgroundColor: "azure",
    border: "1px solid #2e7d32",
    borderRadius: "3px",
    boxShadow: "0 0 20px rgba(0,0,0,0.3)",
    display: "flex",
    flexDirection: "column" as const,
    fontFamily: "Arial",
  },
  titleBar: {
    backgroundColor: "#3c9a3c",
    color: "white",
    fontWeight: "bold" as const,
    fontSize: "13pt",
    padding: "6px 10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexShrink: 0,
  },
  closeBtn: {
    backgroundColor: "#c0392b",
    color: "white",
    border: "1px solid white",
    width: "20px",
    height: "20px",
    lineHeight: "18px",
    textAlign: "center" as const,
    fontWeight: "bold" as const,
    fontSize: "12px",
    cursor: "pointer",
    borderRadius: "2px",
  },
  gridTitle: {
    backgroundColor: "#C7DDFD",
    color: "#2859ac",
    fontWeight: "bold" as const,
    textAlign: "center" as const,
    padding: "10px",
    fontSize: "10pt",
    borderBottom: "1px solid #80a0ff",
    flexShrink: 0,
  },
  scrollBody: {
    flex: 1,
    overflow: "auto" as const,
    border: "1px solid blue",
    borderTop: "none",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    borderBottom: "1px solid #80a0ff"
  },
  th: {
    position: "sticky" as const,
    top: 0,
    backgroundColor: "#C7DDFD",
    color: "#2859ac",
    textAlign: "left" as const,
    padding: "8px",
    borderRight: "1px solid #7d9cf9",
    borderBottom: "1px solid #7d9cf9",
    fontSize: "10pt",
    whiteSpace: "nowrap" as const,
    fontWeight: "normal" as const,

  },
  tdRow: (idx: number) => ({
    backgroundColor: idx % 2 === 0 ? "white" : "#F4FAFF",
  }),
  td: {
    padding: "8px",
    borderRight: "1px solid #7d9cf9",
    fontSize: "10pt",
    whiteSpace: "nowrap" as const,
  },
};
/* -------------------------------------------------------------------------------- */

const formatDate = (dateString: string | null) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB");
};

const calculateTimeDiff = (start: string | null, end: string | null) => {
  if (!start || !end) return "";
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffMs = endDate.getTime() - startDate.getTime();
  if (diffMs < 0) return "";

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
  const mins = Math.floor((diffMs / 1000 / 60) % 60);
  const secs = Math.floor((diffMs / 1000) % 60);

  return `${days > 0 ? days + "." : ""}${hours.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

/* ---------- CHANGED: compares two dd/MM/yyyy strings as real dates ---------- */
const isSameDDMMYYYY = (a: string, b: string) => {
  if (!a || !b) return false;
  const [d1, m1, y1] = a.split("/").map(Number);
  const [d2, m2, y2] = b.split("/").map(Number);
  return d1 === d2 && m1 === m2 && y1 === y2;
};
/* ------------------------------------------------------------------------ */

const EL_StudentStudyHistory: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const sParam = searchParams.get("S") || "";
  const fParam = searchParams.get("F") || "";
  const lParam = searchParams.get("L") || "";

  const [loading, setLoading] = useState<boolean>(true);
  const [alertInfo, setAlertInfo] = useState({ show: false, message: "" });

  const [studentData, setStudentData] = useState<Partial<StudentInfo>>({});
  const [wlpSummary, setWlpSummary] = useState<{
    maths: Partial<WLPSummaryDetails>;
    english: Partial<WLPSummaryDetails>;
  }>({ maths: {}, english: {} });
  const [wlpHistory, setWlpHistory] = useState<WLPHistoryRow[]>([]);
  const [mdTests, setMdTests] = useState<MDTest[]>([]);
  const [ecTests, setEcTests] = useState<ECTest[]>([]);
  const [showWlpDetails, setShowWlpDetails] = useState(false);
  const [wlpDetailTitle, setWlpDetailTitle] = useState("");
  const [wlpDetailRows, setWlpDetailRows] = useState<WLPDetailRow[]>([]);
  const [wlpDetailLoading, setWlpDetailLoading] = useState(false);

  /* ---------- CHANGED: needed to compute CurHistInd exactly like VB ---------- */
  const [currentWkEnd, setCurrentWkEnd] = useState<string>("");
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    const fetchHistoryData = async () => {
      console.log("Starting data fetch with params:", { S: sParam, F: fParam, L: lParam });
      if (!sParam || !fParam || !lParam) {
        setAlertInfo({ show: true, message: "Missing parameters in URL." });
        setLoading(false);
        return;
      }

      const queryParams = new URLSearchParams({ S: sParam, F: fParam, L: lParam });
      const url = `${API_BASE_URL}api/get_student_study_history?${queryParams}`;
      console.log("Fetching data from URL:", url);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 1000000);

      try {
        console.log("Fetching study history for", { S: sParam, F: fParam, L: lParam });
        const resp = await fetch(url, { signal: controller.signal });
        console.log("Fetch response status:", resp.status);
        if (!resp.ok) {
          const text = await resp.text().catch(() => "");
          throw new Error(`HTTP ${resp.status} ${text}`);
        }
        const result = await resp.json();
        console.log("Fetch result:", result);

        if (result && result.status === "success") {
          setStudentData(result.data?.student_info || {});
          setWlpSummary(result.data?.wlp_summary || { maths: {}, english: {} });
          setWlpHistory(result.data?.wlp_history || []);
          setMdTests(result.data?.md_tests || []);
          setEcTests(result.data?.ec_tests || []);
          setCurrentWkEnd(result.data?.current_wk_end || "");
        } else {
          setAlertInfo({ show: true, message: result?.message || "Failed to load data." });
        }
      } catch (err: any) {
        if (err.name === "AbortError") {
          console.warn("Fetch aborted (timeout)");
          setAlertInfo({ show: true, message: "Request timed out. Check network or CORS." });
        } else {
          console.error(err);
          setAlertInfo({ show: true, message: "Network Error while fetching study history." });
        }
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    fetchHistoryData();
  }, [sParam, fParam, lParam]);

  const closeWlpDetails = () => {
    setShowWlpDetails(false);
    setWlpDetailRows([]);
    setWlpDetailTitle("");
  };


  const formatDateTime = (dateValue: string | Date | null | undefined) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);

    if (isNaN(date.getTime())) return "";

    const day = date.getDate();

    const getOrdinal = (n: number) => {
      if (n % 100 >= 11 && n % 100 <= 13) return "th";

      switch (n % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    const month = date.toLocaleString("en-US", {
      month: "long",
    });

    const year = date.getFullYear();

    const time = date.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    return `${day}${getOrdinal(day)} ${month} ${year} ${time}`;
  };
  /* ---------- CHANGED: entire function rewritten to call el_showstudentlp_get_lesson_plan
     with WeekendDate + CurHistInd (computed like VB's fn_ShowLP i=1/i=2) + DoMore ---------- */
  const loadWlpDetails = async (
    weekending: string,
    subject: string,
    doMore: boolean
  ) => {
    setWlpDetailTitle(
      `Lesson Plan for Week Ending : ${weekending}, Subject : ${subject}${doMore ? " (Do More)" : ""}`
    );
    setShowWlpDetails(true);
    setWlpDetailLoading(true);
    setWlpDetailRows([]);

    // matches VB JS: if (weekend date == CurrentWeekEndDate) i = 1 else i = 2
    const curHistInd = isSameDDMMYYYY(weekending, currentWkEnd) ? "1" : "2";

    const queryParams = new URLSearchParams({
      SchoolCode: sParam,
      FamilyCode: fParam,
      Login: lParam,
      Subject: subject,
      WeekendDate: weekending,
      CurHistInd: curHistInd,
      DoMore: doMore ? "1" : "0",
    });

    try {
      const url = `${API_BASE_URL}api/el_showstudentlp/get_lesson_plan?${queryParams}`;
      const resp = await fetch(url);
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const result = await resp.json();
      if (result && result.status === "success") {
        setWlpDetailRows(result.lessons || []);
        if (result.title) setWlpDetailTitle(result.title);
      } else {
        setAlertInfo({ show: true, message: result?.message || "Failed to load WLP details." });
      }
    } catch (err: any) {
      console.error("Failed to load WLP detail", err);
      setAlertInfo({ show: true, message: "Failed to load WLP details." });
    } finally {
      setWlpDetailLoading(false);
    }
  };

  const isThisWeek = (weekending: string) => {
    if (!weekending) return false;

    const [day, month, year] = weekending.split("/").map(Number);

    if (!day || !month || !year) return false;

    const weekEndingDate = new Date(year, month - 1, day);
    weekEndingDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Monday of current week
    const dayOfWeek = today.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() + diffToMonday);
    currentWeekStart.setHours(0, 0, 0, 0);

    // Sunday of current week
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
    currentWeekEnd.setHours(23, 59, 59, 999);

    return (
      weekEndingDate >= currentWeekStart &&
      weekEndingDate <= currentWeekEnd
    );
  };


  const displayValue = (
    value: any,
    weekending: string,
    isMSMins = false
  ) => {
    // MS Mins: ALWAYS show the value, including 0
    if (isMSMins) {
      return value ?? 0;
    }

    // Actual 0:
    // show only for this week's row
    if (value === 0 || value === "0") {
      return isThisWeek(weekending) ? 0 : "";
    }

    // Existing non-zero value
    if (value !== null && value !== undefined && value !== "") {
      return value;
    }

    // Null/empty:
    // show 0 only for this week
    return isThisWeek(weekending) ? 0 : "";
  };
  /* -------------------------------------------------------------------------------------- */

  /* ---------- CHANGED: dropped weekNo param, no longer needed ---------- */
  const handleWlpClick = (
    weekending: string,
    subject: string,
    value: string | undefined,
    doMore: boolean
  ) => {
    if (!value || value.trim() === "") {
      return;
    }
    loadWlpDetails(weekending, subject, doMore);
  };

  const renderWlpCell = (
    value: string | undefined,
    weekending: string,
    subject: string,
    doMore: boolean
  ) => {
    const text = value || "";
    const clickable = text.trim() !== "";

    return (
      <td
        style={{
          ...styles.cell,
          cursor: clickable ? "pointer" : "default",
          color: clickable ? "blue" : "inherit",
        }}
        onClick={clickable ? () => handleWlpClick(weekending, subject, text, doMore) : undefined}
      >
        {clickable ? <span style={styles.actionLink}>{text}</span> : text}
      </td>
    );
  };
  /* --------------------------------------------------------------------- */

  /* ---------- ADDED: single-subject fix. A subject with no activity in a row is blanked (VB behaviour) ---------- */
  const subjectActive = (s?: Partial<WLPStats>) =>
    [s?.ToDo, s?.Complete, s?.Done, s?.ReAssigned].some((v) => Number(v) > 0);

  const renderSubjectCells = (
    s: Partial<WLPStats> | undefined,
    weekending: string,
    subject: string
  ) => {
    const active = subjectActive(s);

    // blank only when there is no data; a real 0 is shown
    const plain = (v: any) => (v === null || v === undefined || v === "" ? "" : v);
    // counts: blank zeros only when the subject has no activity that week (VB clears them)
    const count = (v: any) =>
      !active && (v === 0 || v === "0") ? "" : plain(v);

    const rawDoMore = String(s?.DoMore ?? "");
    const plainDoMore = !active && rawDoMore !== "" && !rawDoMore.includes("/") ? rawDoMore : "";

    return (
      <>
        <td style={styles.cell}>{plain(s?.WkNo)}</td>
        <td style={styles.cell}>{plain(s?.WLPMins)}</td>
        <td style={styles.cell}>{count(s?.ToDo)}</td>
        <td style={styles.cell}>{count(s?.Complete)}</td>
        <td style={styles.cell}>{count(s?.Done)}</td>
        <td style={styles.cell}>
          {active && s?.StDone === "Completed" && (
            <img src={tickGreen} alt="Completed" height="14" />
          )}
        </td>
        <td style={styles.cell}>{count(s?.ReAssigned)}</td>
        {renderWlpCell(active ? s?.WLPDone : "", weekending, subject, false)}
        {active
          ? renderWlpCell(s?.DoMore, weekending, subject, true)
          : <td style={styles.cell}>{plainDoMore}</td>}
        <td style={styles.cell}>{plain(s?.AvgScore)}</td>
        <td style={styles.cell}>{plain(s?.MSMins)}</td>
      </>
    );
  };
  /* ------------------------------------------------------------------------------------------------------------- */

  return (
    <div style={styles.body}>
      {loading && <Loading />}

      <div style={styles.container}>
        <table style={styles.topInfoTable}>
          <tbody>
            <tr>
              <td colSpan={4} style={styles.topInfoTitle}>
                Student Study History
              </td>
            </tr>
            <tr>
              <td style={styles.infoCellLabel}>Student Name:</td>
              <td style={styles.infoCellValue}>
                {studentData.FName || "\u00A0"} {studentData.LName || ""}
              </td>
              <td style={styles.infoCellLabel}>Gender:</td>
              <td style={{ ...styles.infoCellValue, borderRight: "none" }}>
                {studentData.Gender === "B" || studentData.Gender === "M"
                  ? "Boy"
                  : studentData.Gender === "G" || studentData.Gender === "F"
                    ? "Girl"
                    : ""}
              </td>
            </tr>
            <tr>
              <td style={styles.infoCellLabel}>Grade/Year:</td>
              <td style={styles.infoCellValue}>{studentData.Std || "\u00A0"}</td>
              <td style={styles.infoCellLabel}>Date Of Birth(dd/mm/yyyy):</td>
              <td style={{ ...styles.infoCellValue, borderRight: "none" }}>
                {formatDate(studentData.DOB || null)}
              </td>
            </tr>
            <tr>
              <td style={styles.infoCellLabel}>Trophies: Math / English / Total:</td>
              <td style={{ ...styles.infoCellValue }}>
                {studentData.MathsTrophies || 0} / {studentData.EnglishTrophies || 0} / <span style={{ color: "rgb(56, 161, 161)" }}>{studentData.TotalTrophies || 0} </span><span style={{ color: "rgb(56, 161, 161)" }}>{studentData.TrophyClubBonus || <span style={{ color: "rgb(56, 161, 161)" }}>(Club 0, 500)</span>}</span>
              </td>
              <td style={styles.infoCellLabel}>Points Earned / Used / Balance:</td>
              <td style={{ ...styles.infoCellValue, borderRight: "none" }}>
                <span style={{ color: "blue" }}>
                  {studentData.PointsEarned || 0}
                </span>
                {" / "}
                <span style={{ color: "red" }}>
                  {studentData.PointsUsed || 0}
                </span>
                {" / "}
                <span style={{ color: "green" }}>
                  {studentData.CurrentPoints || 0}
                </span>
              </td>
            </tr>
            <tr>
              <td colSpan={4} style={{ ...styles.infoCellValue, borderRight: "none", color: "black", textAlign: "left", fontSize: "10pt" }}>
                <span style={{ color: "black", fontWeight: "normal", marginRight: "100px" }}>WLP (Maths)</span> &nbsp;&nbsp;&nbsp;
                <span>Type</span> <span style={{ color: "red" }}>{wlpSummary.maths?.Type || "-"}</span> &nbsp;&nbsp;&nbsp;
                <span>Created On:</span> <span style={{ color: "red" }}>{wlpSummary.maths?.CreatedOn ?? ""} </span> &nbsp;&nbsp;&nbsp;
                <span>Weeks Elapsed:</span> <span style={{ color: "red" }}>{wlpSummary.maths?.WeeksElapsed || 0} </span> &nbsp;&nbsp;&nbsp;
                <span>Weeks Left:</span> <span style={{ color: "red" }}>{wlpSummary.maths?.WeeksLeft ?? ""} </span> &nbsp;&nbsp;&nbsp;
                <span>Avl. Mts/Week:</span> <span style={{ color: "red" }}>{wlpSummary.maths?.AvlMts ?? ""} </span> &nbsp;&nbsp;&nbsp;
                <span>PrevWLPs:</span> <span style={{ color: "red" }}>{wlpSummary.maths?.PrevWLPs || "-"} </span> &nbsp;&nbsp;&nbsp;
                <span>#Weeks Completed</span> <span style={{ color: "red" }}>{wlpSummary.maths?.Completed || 0}</span>
              </td>
            </tr>
            <tr>
              <td colSpan={4} style={{ ...styles.infoCellValue, borderRight: "none", borderBottom: "none", color: "black", textAlign: "left", fontSize: "10pt" }}>
                <span style={{ color: "black", fontWeight: "normal", marginRight: "95px" }}>WLP (English)</span> &nbsp;&nbsp;&nbsp;
                <span>Type</span> <span style={{ color: "red" }}>{wlpSummary.english?.Type || "-"}</span> &nbsp;&nbsp;&nbsp;
                <span>Created On:</span> <span style={{ color: "red" }}>{wlpSummary.english?.CreatedOn ?? ""} </span> &nbsp;&nbsp;&nbsp;
                <span>Weeks Elapsed:</span> <span style={{ color: "red" }}>{wlpSummary.english?.WeeksElapsed || 0} </span> &nbsp;&nbsp;&nbsp;
                <span>Weeks Left:</span> <span style={{ color: "red" }}>{wlpSummary.english?.WeeksLeft ?? ""} </span> &nbsp;&nbsp;&nbsp;
                <span>Avl. Mts/Week:</span> <span style={{ color: "red" }}>{wlpSummary.english?.AvlMts ?? ""} </span> &nbsp;&nbsp;&nbsp;
                <span>PrevWLPs:</span> <span style={{ color: "red" }}>{wlpSummary.english?.PrevWLPs || "-"} </span> &nbsp;&nbsp;&nbsp;
                <span>#Weeks Completed</span> <span style={{ color: "red" }}>{wlpSummary.english?.Completed || 0}</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div style={styles.scrollableDiv}>
          <table cellPadding={0} cellSpacing={0} style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
              <tr>
                <th colSpan={26} style={styles.topInfoTitle}>WLP Activities History</th>
              </tr>
              <tr style={styles.headerRow}>
                <th rowSpan={2} style={styles.headerCell}>Weekending</th>
                <th colSpan={11} style={{ ...styles.headerCell, borderBottom: "1px solid #7d9cf9" }}>Maths</th>
                <th colSpan={11} style={{ ...styles.headerCell, borderBottom: "1px solid #7d9cf9" }}>English</th>
                <th rowSpan={2} style={{ ...styles.headerCell, borderRight: "none" }}>Logged<br />Mts</th>
              </tr>
              <tr style={styles.headerRow}>
                {[...Array(2)].map((_, i) => (
                  <React.Fragment key={i}>
                    <th style={styles.headerCell}>Wk#</th>
                    <th style={styles.headerCell}>WLP<br />Mins</th>
                    <th style={styles.headerCell}>To<br />Do</th>
                    {/* <th style={styles.headerCell}>Done</th> */}
                    <th style={styles.headerCell}>Done <br />Complete</th>
                    <th style={styles.headerCell}>Done<br />Shifted</th>
                    {/* <th style={styles.headerCell}>St<br />Done</th> */}
                    <th style={styles.headerCell}>St</th>
                    <th style={styles.headerCell}>Done <br />ReAssigned</th>
                    <th style={styles.headerCell}>WLP<br />Done</th>
                    <th style={styles.headerCell}>Do<br />More</th>
                    <th style={styles.headerCell}>Avg<br />Score</th>
                    <th style={styles.headerCell}>MS<br />Mins</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {wlpHistory.length === 0 ? (
                <tr>
                  <td
                    colSpan={26}
                    style={{ padding: "10px", textAlign: "center" }}
                  />
                </tr>
              ) : (
                wlpHistory.map((row, idx) => {
                  const weekending = row.Weekending || "";

                  return (
                    <tr
                      key={idx}
                      style={idx % 2 === 0 ? styles.altRow : styles.row}
                    >
                      <td style={styles.cell}>
                        {weekending}
                      </td>

                      {/* ================= MATHS ================= */}
                      {renderSubjectCells(row.Maths, weekending, "Mathematics")}

                      {/* ================= ENGLISH ================= */}
                      {renderSubjectCells(row.English, weekending, "English")}

                      <td
                        style={{
                          ...styles.cell,
                          borderRight: "none",
                        }}
                      >
                        {row.LoggedMts ?? ""}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>




        {/* ---------- TEST DETAILS ---------- */}
        {/* ---------- TEST DETAILS ---------- */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            width: "100%",
            flex: "0 0 15vh",   // fixed at 25% of the viewport height, no growing or shrinking
            height: "15vh",
            minHeight: 0,
            overflow: "hidden", // each table box scrolls on its own
            backgroundColor: "white",
            marginBottom: "10px",
          }}
        >
          {/* ================= MATHS DOCTOR ================= */}
          <div
            style={{
              width: "50%",
              flex: "0 0 50%",
              boxSizing: "border-box",
              overflowX: "auto",
              border: "1px solid #7d9cf9",
            }}
          >
            <table
              cellPadding={0}
              cellSpacing={0}
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr>
                  <th colSpan={13} style={styles.topInfoTitle}>
                    MATHS DOCTOR TEST DETAILS
                  </th>
                </tr>

                <tr style={{ ...styles.headerRow, textWrap: "nowrap" }}>
                  <th style={styles.headerCell}>#</th>
                  {/* <th style={styles.headerCell}>Source</th> */}
                  <th style={styles.headerCell}>Year</th>
                  <th style={styles.headerCell}>Status</th>
                  {/* <th style={styles.headerCell}>CompletedOn</th> */}
                  <th style={styles.headerCell}>Score</th>
                  <th style={styles.headerCell}>BrickReport</th>
                  <th style={styles.headerCell}>Navigation Report</th>
                  <th style={styles.headerCell}>StartDateTime</th>
                  <th style={styles.headerCell}>EndDateTime</th>
                  <th style={styles.headerCell}>TimeDifference</th>
                  {/* <th style={styles.headerCell}>GapReport</th> */}
                  {/* <th style={styles.headerCell}>WPReport</th> */}
                </tr>
              </thead>

              <tbody>
                {mdTests.map((row, index) => {
                  const isFamilyAcc = !row.SetByTutor;

                  const source = isFamilyAcc
                    ? "Family A/c"
                    : row.SetOn
                      ? `Set by Tutor on: ${formatDate(row.SetOn)}`
                      : "Set by Tutor";

                  const status = isFamilyAcc
                    ? row.TestDoneOn
                      ? "Completed"
                      : "Started"
                    : row.TestDoneOn
                      ? "Completed"
                      : row.StartedOn
                        ? "Started"
                        : "Not Started";

                  const timeDiff = calculateTimeDiff(
                    row.StartedOn,
                    row.TestDoneOn
                  );

                  const showLinks = !!row.ReportNo && (isFamilyAcc || !!row.TestDoneOn);

                  return (
                    <tr
                      key={index}
                      style={{
                        ...(index % 2 === 0 ? styles.altRow : styles.row),
                        textWrap: "nowrap",
                      }}
                    >
                      <td style={styles.cell}>{index + 1}</td>

                      {/* <td style={{ ...styles.cell, textAlign: "left" }}>
                {source}
              </td> */}

                      <td style={styles.cell}>{row.GradeYear}</td>

                      <td style={styles.cell}>{status}</td>

                      {/* <td style={styles.cell}>
                {formatDate(row.TestDoneOn)}
              </td> */}

                      <td style={styles.cell}>
                        {row.ScoreA != null && row.ScoreA !== ""
                          ? Number(row.ScoreA).toFixed(2)
                          : ""}
                      </td>

                      <td style={styles.cell}>
                        {showLinks && (
                          <span
                            style={styles.actionLink}
                            onClick={() =>
                              navigate(
                                `/MathsDoctor/Reports/StillReport?SchoolCode=${sParam}&FamilyCode=${fParam}&Login=${lParam}&ReportNo=${row.ReportNo}`
                              )
                            }
                          >
                            View Report
                          </span>
                        )}
                      </td>

                      <td style={styles.cell}>
                        {showLinks && (
                          <span
                            style={styles.actionLink}
                            onClick={() =>
                              navigate(
                                `/MD_Reports/MD_NavReport?StuSch=EL_${sParam}&StuFam=${fParam}&StudentId=${lParam}&ReportNo=${row.ReportNo}&Source=StudyHistory`
                              )
                            }
                          >
                            View Report
                          </span>
                        )}
                      </td>

                      <td style={styles.cell}>
                        {formatDateTime(row.StartedOn)}
                      </td>

                      <td style={styles.cell}>
                        {formatDateTime(row.TestDoneOn)}
                      </td>

                      <td style={styles.cell}>{timeDiff}</td>

                      {/* <td style={styles.cell}></td> */}

                      {/* <td style={styles.cell}></td> */}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>


          {/* ================= ENGLISH CHECK ================= */}
          <div
            style={{
              width: "50%",
              flex: "0 0 50%",
              boxSizing: "border-box",
              overflowX: "auto",
              border: "1px solid #7d9cf9"
            }}
          >
            <table
              cellPadding={0}
              cellSpacing={0}
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr>
                  <th colSpan={13} style={styles.topInfoTitle}>
                    ENGLISH CHECK TEST DETAILS
                  </th>
                </tr>

                <tr style={{ ...styles.headerRow, textWrap: "nowrap" }}>
                  <th style={styles.headerCell}>#</th>
                  {/* <th style={styles.headerCell}>Source</th> */}
                  <th style={styles.headerCell}>Year</th>
                  <th style={styles.headerCell}>Status</th>
                  {/* <th style={styles.headerCell}>CompletedOn</th> */}
                  <th style={styles.headerCell}>Score</th>
                  <th style={styles.headerCell}>BrickReport</th>
                  <th style={styles.headerCell}>Navigation Report</th>
                  <th style={styles.headerCell}>StartDateTime</th>
                  <th style={styles.headerCell}>EndDateTime</th>
                  <th style={styles.headerCell}>TimeDifference</th>
                  {/* <th style={styles.headerCell}>GapReport</th> */}
                  {/* <th style={styles.headerCell}>WPReport</th> */}
                </tr>
              </thead>

              <tbody>
                {ecTests.map((row, index) => {
                  const isFamilyAcc = !row.SetByTutor;

                  const source = isFamilyAcc
                    ? "Family A/c"
                    : row.SetOn
                      ? `Set by Tutor on: ${formatDate(row.SetOn)}`
                      : "Set by Tutor";

                  const status = isFamilyAcc
                    ? row.TestDoneOn
                      ? "Completed"
                      : "Started"
                    : row.TestDoneOn
                      ? "Completed"
                      : row.StartedOn
                        ? "Started"
                        : "Not Started";

                  const timeDiff = calculateTimeDiff(
                    row.StartedOn,
                    row.TestDoneOn
                  );

                  const showLinks = !!row.ReportNo && (isFamilyAcc || !!row.TestDoneOn);

                  return (
                    <tr
                      key={index}
                      style={{
                        ...(index % 2 === 0 ? styles.altRow : styles.row),
                        textWrap: "nowrap",
                      }}
                    >
                      <td style={styles.cell}>{index + 1}</td>

                      {/* <td style={{ ...styles.cell, textAlign: "left" }}>
                        {source}
                      </td> */}

                      <td style={styles.cell}>{row.GradeYear}</td>

                      <td style={styles.cell}>{status}</td>

                      {/* <td style={styles.cell}>
                        {formatDate(row.TestDoneOn)}
                      </td> */}

                      <td style={styles.cell}>
                        {row.ScoreA != null && row.ScoreA !== ""
                          ? Number(row.ScoreA).toFixed(2)
                          : ""}
                      </td>

                      <td style={styles.cell}>
                        {showLinks && (
                          <span
                            style={styles.actionLink}
                            onClick={() =>
                              navigate(
                                `/EnglishCheck/Reports/EStillReport?SchoolCode=${sParam}&FamilyCode=${fParam}&Login=${lParam}&ReportNo=${row.ReportNo}`
                              )
                            }
                          >
                            View Report
                          </span>
                        )}
                      </td>

                      <td style={styles.cell}>
                        {showLinks && (
                          <span
                            style={styles.actionLink}
                            onClick={() =>
                              navigate(
                                `/EC_Reports/EC_NavReport?StuSch=EL_${sParam}&StuFam=${fParam}&StudentId=${lParam}&ReportNo=${row.ReportNo}&Source=StudyHistory`
                              )
                            }
                          >
                            View Report
                          </span>
                        )}
                      </td>

                      {/* <td style={styles.cell}>
                        {row.StartedOn
                          ? new Date(row.StartedOn).toLocaleString()
                          : ""}
                      </td>

                      <td style={styles.cell}>
                        {row.TestDoneOn
                          ? new Date(row.TestDoneOn).toLocaleString()
                          : ""}
                      </td> */}


                      <td style={styles.cell}>
                        {formatDateTime(row.StartedOn)}
                      </td>

                      <td style={styles.cell}>
                        {formatDateTime(row.TestDoneOn)}
                      </td>

                      <td style={styles.cell}>{timeDiff}</td>

                      {/* <td style={styles.cell}></td> */}

                      {/* <td style={styles.cell}></td> */}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ---------- CHANGED: modal table columns reordered/renamed to match VB grid exactly ---------- */}
      {/* ---------- CHANGED: replicate gfn_Popup(theme=green) exactly ---------- */}
      {showWlpDetails && (
        <div style={wlpModalStyles.overlay} onClick={closeWlpDetails}>

          <div style={wlpModalStyles.popup} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                backgroundImage: `url(${popimg}), url(${popbg})`,
                backgroundRepeat: "no-repeat, no-repeat",
                backgroundPosition: "left center, right center",
                backgroundSize: "30% 100%, 70% 100%",
                height: "16px",
                color: "white",
                padding: "7px 0px",
                fontWeight: "bold",
                fontSize: "12pt",
                fontFamily: "arial",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                // marginBottom: "10px",
              }}
            >
              <span style={{ marginLeft: "10px" }}>
                WLP DETAILS
              </span>
              <img
                src={popclose}
                alt="close"
                height="30px"
                style={{ cursor: "pointer" }}
                onClick={closeWlpDetails}
              />
            </div>
            {/* <div style={wlpModalStyles.titleBar}>
     
              <span style={wlpModalStyles.closeBtn} onClick={closeWlpDetails}>X</span>
            </div> */}

            <div style={wlpModalStyles.gridTitle}>{wlpDetailTitle}</div>

            <div style={wlpModalStyles.scrollBody}>
              {wlpDetailLoading ? (
                <div style={{ textAlign: "center", padding: "40px 20px" }}>
                  <div style={{
                    display: "inline-block",
                    width: "50px",
                    height: "50px",
                    border: "4px solid #f3f3f3",
                    borderTop: "4px solid #3498db",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }} />
                  <div style={{ marginTop: "15px", color: "#666", fontSize: "14px" }}>Loading lesson plan...</div>
                  <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
                </div>
              ) : wlpDetailRows.length === 0 ? (
                <div style={{ padding: "16px", textAlign: "center" }}>No details available for this week.</div>
              ) : (
                <table style={wlpModalStyles.table}>
                  <thead>
                    <tr>
                      <th style={wlpModalStyles.th}>#</th>
                      <th style={wlpModalStyles.th}>Type</th>
                      <th style={wlpModalStyles.th}>Activity</th>
                      <th style={wlpModalStyles.th}>Score</th>
                      <th style={wlpModalStyles.th}>Completed Flag</th>
                      <th style={wlpModalStyles.th}>How Shifted</th>
                      <th style={wlpModalStyles.th}>Level</th>
                      <th style={wlpModalStyles.th}>DicFileName</th>
                      <th style={wlpModalStyles.th}>LessonCode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wlpDetailRows.map((detail, index) => (
                      <tr key={index} style={wlpModalStyles.tdRow(index)}>
                        <td style={wlpModalStyles.td}>{index + 1}</td>
                        <td style={wlpModalStyles.td}>{detail.Type || ""}</td>
                        <td style={wlpModalStyles.td}>{detail.LessonName || ""}</td>
                        <td style={wlpModalStyles.td}>{detail.MDScore || ""}</td>
                        <td style={wlpModalStyles.td}>{detail.CompletedFlag || ""}</td>
                        <td style={wlpModalStyles.td}>{detail.HowShifted || ""}</td>
                        <td style={wlpModalStyles.td}>{detail.Level || ""}</td>
                        <td style={wlpModalStyles.td}>{detail.DicFileName || ""}</td>
                        <td style={wlpModalStyles.td}>{detail.LessonCode || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ------------------------------------------------------------------------------- */}
      {/* ---------------------------------------------------------------------------------------- */}

      {alertInfo.show && (
        <CustomAlert message={alertInfo.message} onClose={() => setAlertInfo({ ...alertInfo, show: false })} />
      )}
    </div>
  );
};

export default EL_StudentStudyHistory;